/**
 * The layer filter, applied in the browser for file-backed layers.
 *
 * WHY THIS EXISTS
 * `Layer.filter` is an expression written for a SERVICE — CQL_FILTER for WMS,
 * a `where` clause for ArcGIS. A layer imported from a GeoJSON file has no
 * service behind it, so that string reached nothing: "Apply Filter" wrote an
 * expression, the chip appeared, and every feature stayed on the map.
 *
 * The fix is not to parse the string back. The Filter tool builds it from
 * structured clauses precisely so the same expression can become either a
 * server request or a client-side predicate (see attributeQuery.ts), so the
 * clauses are kept on the layer and evaluated here with `matchesClauses` —
 * the same predicate the Query and Select tools already run.
 *
 * WHERE IT APPLIES
 * Everywhere a service-backed layer would have sent `filter` to its service:
 * the map, the attribute table, and Select's hit-testing. Deliberately NOT in
 * `loadLayerFeatures`, which every backend reads unfiltered — the Query tool
 * and the value suggestions have to offer what the layer HOLDS, not what the
 * current filter leaves behind, or a filter could never be widened again.
 */

import { matchesClauses } from '../components/layers/attributeQuery';
import type { Layer } from '../types';

/** True when this layer has a filter the browser can actually apply. */
export function hasLocalFilter(layer: Pick<Layer, 'filterClauses'>): boolean {
  return Boolean(layer.filterClauses && layer.filterClauses.length > 0);
}

/** One feature's verdict, honouring the tool's "except these" switch. */
function keeps(layer: Pick<Layer, 'filterClauses' | 'filterInvert'>, feature: GeoJSON.Feature) {
  const matched = matchesClauses(
    (feature.properties ?? {}) as Record<string, unknown>,
    layer.filterClauses ?? [],
  );
  return layer.filterInvert ? !matched : matched;
}

/**
 * The features a file-backed layer should show.
 *
 * An unfiltered layer returns its own array untouched, so nothing downstream
 * re-renders just because this ran.
 */
export function filteredLocalFeatures(
  layer: Pick<Layer, 'localData' | 'filterClauses' | 'filterInvert'>,
): GeoJSON.Feature[] {
  const features = layer.localData?.features ?? [];
  if (!hasLocalFilter(layer)) return features;
  return features.filter((feature) => keeps(layer, feature));
}

/**
 * The same, as a FeatureCollection for the renderer.
 *
 * Returns the ORIGINAL object when there is no filter. Identity matters here:
 * the map layer is rebuilt whenever this value changes, so handing back a new
 * collection every render would tear down and re-add the layer continuously.
 */
export function filteredLocalData(
  layer: Pick<Layer, 'localData' | 'filterClauses' | 'filterInvert'>,
): GeoJSON.FeatureCollection {
  const data = (layer.localData ?? {
    type: 'FeatureCollection',
    features: [],
  }) as GeoJSON.FeatureCollection;

  if (!hasLocalFilter(layer)) return data;
  return { ...data, features: data.features.filter((feature) => keeps(layer, feature)) };
}
